import { WhitelistListResponse, WhitelistEntry, WhitelistProfile } from "../types/whitelist";
import type { FieldOption } from "../types/fieldOption";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string;
  headers?: Record<string, string>;
}

export interface ApiError extends Error {
  status?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function request(path: string, { method = "GET", body, token, headers }: RequestOptions = {}): Promise<any> {
  // FormData (upload de arquivo): não faz JSON.stringify nem força
  // Content-Type — o browser define o multipart/form-data com boundary sozinho.
  const isFormData = body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
  });

  const text = await response.text();
  const data = text ? safeParseJson(text) : null;

  if (!response.ok) {
    const detail = data?.detail ?? data?.message;
    const message =
      (typeof detail === "string" && detail) ||
      (Array.isArray(detail) && detail[0]?.msg) ||
      `Erro ${response.status} ao chamar ${path}`;
    const error: ApiError = new Error(message);
    error.status = response.status;
    throw error;
  }
  return data;
}

function safeParseJson(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function loginWithGoogle(idToken: string) {
  return request("/api/v1/auth/google", {
    method: "POST",
    body: { id_token: idToken },
  });
}

export function getCurrentUser(token: string) {
  return request("/api/v1/auth/me", { token });
}

export function logout(token: string) {
  return request("/api/v1/auth/logout", { method: "POST", token });
}

export function requestCandidateCode(email: string) {
  return request("/api/v1/auth/candidate/request-code", {
    method: "POST",
    body: { email },
  });
}

export function verifyCandidateCode(email: string, code: string) {
  return request("/api/v1/auth/candidate/verify-code", {
    method: "POST",
    body: { email, code },
  });
}


export function getMyWhitelistProfile(token: string): Promise<WhitelistProfile> {
  return request("/api/v1/whitelist/me", { token });
}

export function getWhitelistEntries(token: string): Promise<WhitelistListResponse> {
  return request("/api/v1/whitelist/", { token });
}

export interface HrProcessStats {
  total: number;
  aguardando_rh: number;
  em_andamento: number;
  concluido: number;
  cancelado: number;
}

export interface HrProcessDashboardResponse {
  entries: WhitelistEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface HrProcessFilters {
  recruiter?: string[];
  status?: string;
  manager?: string[];
  cost_center?: string[];
  concluded_from?: string;
  concluded_to?: string;
  cancelled_from?: string;
  cancelled_to?: string;
  admission_from?: string;
  admission_to?: string;
  dp_finished?: boolean;
}

export function getHrProcessDashboard(
  token: string,
  search?: string,
  page?: number,
  page_size?: number,
  filters?: HrProcessFilters,
): Promise<HrProcessDashboardResponse> {
  const q: string[] = [];
  if (search) q.push(`search=${encodeURIComponent(search)}`);
  if (page) q.push(`page=${page}`);
  if (page_size) q.push(`page_size=${page_size}`);
  if (filters?.recruiter?.length) q.push(`recruiter=${encodeURIComponent(filters.recruiter.join(","))}`);
  if (filters?.status) q.push(`status=${encodeURIComponent(filters.status)}`);
  if (filters?.manager?.length) q.push(`manager=${encodeURIComponent(filters.manager.join(","))}`);
  if (filters?.cost_center?.length) q.push(`cost_center=${encodeURIComponent(filters.cost_center.join(","))}`);
  if (filters?.concluded_from) q.push(`concluded_from=${encodeURIComponent(filters.concluded_from)}`);
  if (filters?.concluded_to) q.push(`concluded_to=${encodeURIComponent(filters.concluded_to)}`);
  if (filters?.cancelled_from) q.push(`cancelled_from=${encodeURIComponent(filters.cancelled_from)}`);
  if (filters?.cancelled_to) q.push(`cancelled_to=${encodeURIComponent(filters.cancelled_to)}`);
  if (filters?.admission_from) q.push(`admission_from=${encodeURIComponent(filters.admission_from)}`);
  if (filters?.admission_to) q.push(`admission_to=${encodeURIComponent(filters.admission_to)}`);
  if (filters?.dp_finished !== undefined) q.push(`dp_finished=${filters.dp_finished}`);
  const params = q.length ? `?${q.join("&")}` : "";
  return request(`/api/v1/whitelist/hr-processes${params}`, { token });
}

export interface HrFilterOptions {
  recruiters: { email: string; name: string }[];
  managers: string[];
  cost_centers: string[];
}

export function getHrFilters(token: string): Promise<HrFilterOptions> {
  return request(`/api/v1/whitelist/hr-processes/filters`, { token });
}

/** Big numbers — contagens sobre TODOS os processos RH salvos, sem filtros. */
export function getHrProcessStats(token: string): Promise<HrProcessStats> {
  return request(`/api/v1/whitelist/hr-processes/stats`, { token });
}

export interface HrProcessAuditEntry {
  id: number;
  candidate_email: string;
  candidate_name: string | null;
  actor_email: string;
  actor_name: string | null;
  field: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

export interface HrProcessAuditListResponse {
  entries: HrProcessAuditEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface HrAuditCandidate {
  email: string;
  name: string | null;
}

export function getHrAuditFeed(
  token: string,
  page = 1,
  pageSize = 20,
  search?: string,
  dateFrom?: string,
  dateTo?: string,
  candidates?: string[],
): Promise<HrProcessAuditListResponse> {
  const q: string[] = [`page=${page}`, `page_size=${pageSize}`];
  if (search) q.push(`search=${encodeURIComponent(search)}`);
  if (dateFrom) q.push(`date_from=${dateFrom}`);
  if (dateTo) q.push(`date_to=${dateTo}`);
  if (candidates?.length) q.push(`candidate=${candidates.map(encodeURIComponent).join(",")}`);
  return request(`/api/v1/whitelist/hr-processes/audit?${q.join("&")}`, { token });
}

export function getHrAuditCandidates(token: string): Promise<HrAuditCandidate[]> {
  return request(`/api/v1/whitelist/hr-processes/audit/candidates`, { token });
}

export function getCandidateAudit(token: string, email: string, limit = 20): Promise<HrProcessAuditEntry[]> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/audit?limit=${limit}`, { token });
}

export interface WhitelistEntryCreate {
  email: string;
  candidate_full_name?: string | null;
  cpf_number?: string | null;
  candidate_phone?: string | null;
  responsible_manager?: string | null;
  is_pj?: boolean | null;
}

export function addWhitelistEntry(token: string, payload: WhitelistEntryCreate): Promise<WhitelistEntry> {
  return request("/api/v1/whitelist/", {
    method: "POST",
    body: payload,
    token,
  });
}

export interface WhitelistEntryPatch {
  is_pj?: boolean | null;
  is_tech?: boolean | null;
  responsible_manager?: string | null;
  recruiter?: string | null;
  recruiter_name?: string | null;
  admission_exam_completed?: boolean;
  job_title?: string | null;
  job_activities?: string | null;
  salary?: string | null;
  meal_voucher?: string | null;
  cost_center?: string | null;
  workplace?: string | null;
  work_schedule?: string | null;
  work_start_time?: string | null;
  work_end_time?: string | null;
  admission_date?: string | null;
}

export function patchWhitelistEntry(token: string, email: string, patch: WhitelistEntryPatch): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}`, {
    method: "PATCH",
    body: patch,
    token,
  });
}

export function removeWhitelistEntry(token: string, email: string): Promise<void> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}`, {
    method: "DELETE",
    token,
  });
}

export function setDpFinished(token: string, email: string, finished: boolean): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/dp-finished`, {
    method: "PATCH",
    body: { finished },
    token,
  });
}

export function setWhitelistCandidateEmail(
  token: string,
  currentEmail: string,
  realEmail: string,
): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(currentEmail)}/set-email`, {
    method: "PATCH",
    body: { real_email: realEmail },
    token,
  });
}

export interface EmailLog {
  id: number;
  to_email: string;
  candidate_name: string | null;
  email_type: string;
  status: string;  // "pending" | "sent" | "failed"
  attempts: number;
  last_error: string | null;
  created_at: string;
  sent_at: string | null;
}

export function retryEmailLog(token: string, logId: number): Promise<EmailLog> {
  return request(`/api/v1/email-logs/${logId}/retry`, { method: "POST", token });
}

export function resendWelcomeEmail(token: string, email: string): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/resend-welcome`, { method: "POST", token });
}

export function getWhitelistEntry(token: string, email: string): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}`, { token });
}

export interface Collaborator {
  id: number;
  name: string;
  email: string;
}

export function listCollaborators(token: string, search?: string): Promise<Collaborator[]> {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  return request(`/api/v1/users/collaborators${params}`, { token });
}

export interface BenefitsEmailPreferences {
  wants_wellhub?: boolean | null;
  wants_plano_saude?: boolean | null;
  wants_plano_odonto?: boolean | null;
}

export function sendBenefitsEmail(
  token: string,
  email: string,
  preferences?: BenefitsEmailPreferences,
): Promise<WhitelistEntry> {
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/send-benefits-email`, {
    method: "POST",
    body: preferences ?? {},
    token,
  });
}

/** Envia ao candidato a convocação do exame admissional com a guia de
 * agendamento anexada. multipart/form-data — campo `file` (guia) obrigatório. */
export function sendExamConvocation(token: string, email: string, file: File): Promise<WhitelistEntry> {
  const formData = new FormData();
  formData.append("file", file);
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/send-exam-email`, {
    method: "POST",
    body: formData,
    token,
  });
}

/** Faz upload do ASO ao starter-pack — distinto do envio da guia de agendamento. */
export function uploadAso(token: string, email: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  return request(`/api/v1/whitelist/${encodeURIComponent(email)}/upload-aso`, {
    method: "POST",
    body: formData,
    token,
  });
}

// ── Opções configuráveis dos dropdowns do processo interno de RH ────────────

export function getHrFieldOptions(fieldKey: string, token: string): Promise<FieldOption[]> {
  return request(`/api/v1/hr-field-options/${encodeURIComponent(fieldKey)}`, { token });
}

export function createHrFieldOption(
  fieldKey: string,
  value: string,
  token: string,
): Promise<FieldOption> {
  return request(`/api/v1/hr-field-options/${encodeURIComponent(fieldKey)}`, {
    method: "POST",
    body: { value },
    token,
  });
}

export function updateHrFieldOption(
  optionId: number,
  value: string,
  token: string,
): Promise<FieldOption> {
  return request(`/api/v1/hr-field-options/${optionId}`, {
    method: "PATCH",
    body: { value },
    token,
  });
}

export function deleteHrFieldOption(optionId: number, token: string): Promise<void> {
  return request(`/api/v1/hr-field-options/${optionId}`, { method: "DELETE", token });
}


// ── Métricas ─────────────────────────────────────────────────────────────────
export interface MetricCard {
  key: string;
  label: string;
  value: number | null;
  previous: number | null;
  variation_pct: number | null;
  format: "int" | "percent" | "hours";
  hint: string | null;
}

export interface MetricChart {
  type: "line" | "bar" | "horizontalBar" | "doughnut";
  title: string;
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

export interface MetricSection {
  cards: MetricCard[];
  charts: Record<string, MetricChart>;
  error?: string;
}

export interface MetricsDashboard {
  /** dia/semana/mês — derivada pelo backend a partir do tamanho do range, não escolhida no front. */
  granularity: "day" | "week" | "month";
  range: { start: string; end: string; previous_start: string; previous_end: string };
  sections_meta: { key: string; label: string }[];
  sections: Record<string, MetricSection>;
  generated_at: string;
}

/** dateFrom/dateTo no formato YYYY-MM-DD. Omitidos = mês atual (padrão do backend). */
export function getMetricsDashboard(
  token: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<MetricsDashboard> {
  const params = new URLSearchParams();
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  const qs = params.toString();
  return request(`/api/v1/metrics/dashboard${qs ? `?${qs}` : ""}`, { token });
}


/** Login local sem Google — a rota só existe no backend com ENV=development.
 *  Chamado apenas pelo bloco de desenvolvimento da tela de login, que o Vite
 *  remove do bundle de produção via `import.meta.env.DEV`. */
export function devLogin(email: string): Promise<any> {
  return request("/api/v1/auth/dev-login", { method: "POST", body: { email } });
}
